import {
  defaultWidth,
  defaultHeight,
  offsetActivityY,
  offsetRefinementX,
  offsetRefinementY,
  spacing,
} from "./defaults";

import {
  findChildByNodeId,
  getAbsolutePosition,
  getNodes,
  getMainThread,
} from "./radTreeUtils";

/*
 * Computes the positions of activities and refinements in a RAD tree.
 * Takes into account connections between interaction activities across threads.
 * Returns a new RAD tree with updated positions.
 */

const adjustAbsolutePositions = ({ tree }) => {
  // Helper function to recursively process threads at any level
  const processThread = (thread) => {
    const threadAbsPos = getAbsolutePosition(tree, thread.node.id);

    const updatedChildren = thread.children.map((child) => {
      const childAbsPos = getAbsolutePosition(tree, child.node.id);
      //console.log("ABS POS", child.node.id, child.node.class, childAbsPos);

      if (child.node.class === "activity") {
        return {
          ...child,
          node: {
            ...child.node,
            absPos: { x: childAbsPos.x, y: childAbsPos.y },
          },
        };
      } else {
        // refinement - recursively process all subthreads
        const updatedSubthreads = child.children.map(processThread);

        return {
          ...child,
          node: {
            ...child.node,
            absPos: { x: childAbsPos.x, y: childAbsPos.y },
          },
          children: updatedSubthreads,
        };
      }
    });

    return {
      ...thread,
      node: {
        ...thread.node,
        absPos: { x: threadAbsPos.x, y: threadAbsPos.y },
      },
      children: updatedChildren,
    };
  };

  const treeChildren = tree.children.map((role) => {
    const roleAbsPos = getAbsolutePosition(tree, role.node.id);
    const roleChildren = role.children.map(processThread);

    return {
      ...role,
      node: {
        ...role.node,
        absPos: { x: roleAbsPos.x, y: roleAbsPos.y },
      },
      children: roleChildren,
    };
  });

  return { ...tree, children: treeChildren };
};

const computeContainerSize = (container, padding = 0) => {
  if (container.children.length === 0) {
    return {
      width: container.node.size?.width || defaultWidth + padding,
      height: container.node.size?.height || defaultHeight + padding,
    };
  }
  const { xMax, yMax } = container.children.reduce(
    (
      expanse,
      {
        node: {
          position: { x, y },
          size: { width, height },
        },
      }
    ) => ({
      xMax: Math.max(expanse.xMax, x + width + padding),
      yMax: Math.max(expanse.yMax, y + height),
    }),
    { xMax: -Infinity, yMax: -Infinity }
  );

  return {
    width: xMax + padding,
    height: yMax + padding,
  };
};

const computeContainerSizes = (radTree) => {
  // A final pass of the tree to set the role and role thread sizes
  radTree.children.forEach((role) => {
    role.children.forEach((thread) => {
      // Compute thread size based on children
      thread.node.size = computeContainerSize(thread, 60);
    });
    // Compute role size based on threads
    role.node.size = computeContainerSize(role, 20);
  });
  return radTree;
};

const computeNodeSize = (node) => {
  if (node.children.length === 0) {
    return node.size || { width: defaultWidth, height: defaultHeight };
  }
  const childSizes = node.children.map((child) => {
    const childSize =
      child.node.type === "void"
        ? { width: 0, height: 0 }
        : computeNodeSize(child);
    // Ensure the size is assigned to the child node
    child.node.size = childSize;
    return childSize;
  });
  const nodeWidth = computeNodeWidth(childSizes);
  const nodeHeight = computeNodeHeight(node, childSizes);
  return { width: nodeWidth, height: nodeHeight };
};

const computeNodeWidth = (childSizes) => {
  // sum the child widths
  const size = childSizes.reduce((sum, s) => sum + s.width + spacing, 0);
  return size;
};

const computeNodeHeight = (node, childSizes) => {
  if (node.node.class === "refinement") {
    const size =
      Math.max(defaultHeight, ...childSizes.map((s) => s.height)) +
      offsetRefinementY;
    return size;
  } else {
    const size = childSizes.reduce(
      (sum, s) => sum + s.height + offsetActivityY,
      0
    );
    return size;
  }
};

const computeSizes = (radTree) => {
  radTree.children.forEach((role) => {
    role.children.forEach((thread) => {
      // Process only the children of threads
      thread.children = thread.children.map((child) => {
        // Compute the size for each child node
        const updatedSize = computeNodeSize(child);
        return {
          ...child,
          node: {
            ...child.node,
            size: updatedSize,
          },
        };
      });
    });
  });

  return radTree;
};

// Helper function to recalculate positions from incremental offsets
const recalculateThreadPositions = (thread) => {
  let currentY = 0;

  const updatedChildren = thread.children.map((child) => {
    if (child.node.type === "void") return child;

    currentY += child.node.offsetFromPrevious || 0;

    const updatedChild = {
      ...child,
      node: {
        ...child.node,
        position: {
          ...child.node.position,
          y: currentY,
        },
      },
    };

    // If it's a refinement, recursively update subthreads and recalculate refinement size
    if (child.node.class === "refinement") {
      updatedChild.children = child.children.map(recalculateThreadPositions);

      // Recalculate refinement container size based on updated subthreads
      const maxSubthreadHeight = Math.max(
        ...updatedChild.children.map((subthread) => {
          const lastChild = subthread.children[subthread.children.length - 1];
          return lastChild
            ? lastChild.node.position.y + lastChild.node.size.height
            : 0;
        })
      );

      // Update refinement size to accommodate subthreads
      updatedChild.node.size = {
        ...updatedChild.node.size,
        height: Math.max(
          updatedChild.node.size.height,
          maxSubthreadHeight + offsetRefinementY
        ),
      };
    }

    currentY += updatedChild.node.size.height;
    return updatedChild;
  });

  // Update thread size based on children
  const threadHeight = currentY;
  const updatedThread = {
    ...thread,
    children: updatedChildren,
    node: {
      ...thread.node,
      size: {
        ...thread.node.size,
        height: threadHeight,
      },
    },
  };

  return updatedThread;
};

// Helper function to find the root thread containing a given node
const findRootThread = (tree, nodeId) => {
  let currentNode = findChildByNodeId(tree, nodeId);
  if (!currentNode) return null;

  // Walk up the hierarchy to find the main thread
  while (currentNode && currentNode.node.parent_id) {
    const parent = findChildByNodeId(tree, currentNode.node.parent_id);
    if (!parent || parent.node.class === "role") break;
    currentNode = parent;
  }

  return currentNode;
};

const adjustAllConnectionsInSequence = ({ tree, connections }) => {
  if (!connections || connections.length === 0) return tree;

  const treeCopy = JSON.parse(JSON.stringify(tree));

  // Sort connection groups by temporal order
  const sortedConnectionGroups = connections
    .map((connectionGroup) => {
      const connectedInteractions = connectionGroup.interactions
        .map((id) => findChildByNodeId(treeCopy, id))
        .filter(Boolean)
        .map((node) => {
          const nodeInfo = getNodes({
            tree: treeCopy,
            className: "activity",
            type: "interaction",
            parent_id: node.node.thread_id,
          }).find(({ node: n }) => n.id === node.node.id);

          return {
            node: node.node,
            absPos: getAbsolutePosition(treeCopy, node.node.id),
            sequence: nodeInfo?.precedingInteractions || 0,
          };
        });

      return {
        ...connectionGroup,
        connectedInteractions,
        minY: Math.min(...connectedInteractions.map((i) => i.absPos.y)),
        maxY: Math.max(...connectedInteractions.map((i) => i.absPos.y)),
      };
    })
    .sort((a, b) => a.minY - b.minY);

  // Validate primary connection groups (sequence=0) are disjoint
  const primaryConnectionGroups = sortedConnectionGroups.filter(
    ({ connectedInteractions }) =>
      connectedInteractions.every((info) => info.sequence === 0)
  );

  if (primaryConnectionGroups.length > 1) {
    console.log(
      "Multiple primary connection groups found. Validating they are disjoint."
    );

    // Ensure the primary connection groups are disjoint (no shared interactions)
    const allPrimaryInteractionIds = new Set();
    for (const { connectedInteractions, id } of primaryConnectionGroups) {
      for (const info of connectedInteractions) {
        if (allPrimaryInteractionIds.has(info.node.id)) {
          const errorMsg = `Error: Overlapping interactions found in primary connection groups. Connection group ${id} shares interaction ${info.node.id} with another primary group. RAD validation required.`;
          console.error(errorMsg);
          throw new Error(errorMsg);
        }
        allPrimaryInteractionIds.add(info.node.id);
      }
    }
    console.log(
      "Primary connection groups are disjoint. Proceeding with adjustment."
    );
  }

  let currentTree = treeCopy;

  sortedConnectionGroups.forEach((connectionGroup) => {
    // Recalculate positions for this group
    const currentConnectedInteractions = connectionGroup.interactions
      .map((id) => findChildByNodeId(currentTree, id))
      .filter(Boolean)
      .map((node) => ({
        node: node.node,
        absPos: getAbsolutePosition(currentTree, node.node.id),
      }));

    const targetY = Math.max(
      ...currentConnectedInteractions.map((i) => i.absPos.y)
    );

    currentConnectedInteractions.forEach((info) => {
      if (info.absPos.y === targetY) return;

      const offsetY = targetY - info.absPos.y;

      const nodeInfo = getNodes({
        tree: currentTree,
        className: "activity",
        type: "interaction",
        parent_id: info.node.thread_id,
      }).find(({ node }) => node.id === info.node.id);

      if (nodeInfo?.sequence === 0) {
        // Move entire thread by adjusting its position
        const mainThreadId = getMainThread(
          currentTree,
          info.node.thread_id
        )?.id;
        const threadNode = findChildByNodeId(currentTree, mainThreadId);

        if (threadNode) {
          threadNode.node.position = {
            ...threadNode.node.position,
            y: threadNode.node.position.y + offsetY,
          };
          console.log(`Moved thread ${mainThreadId} by ${offsetY}`);
        }
      } else {
        // Adjust individual interaction's offsetFromPrevious
        const interactionNode = findChildByNodeId(currentTree, info.node.id);
        if (interactionNode) {
          // Increase the offset from the previous activity
          interactionNode.node.offsetFromPrevious =
            (interactionNode.node.offsetFromPrevious || offsetActivityY) +
            offsetY;

          /*           console.log(
            `Adjusted interaction ${info.node.id} offset by ${offsetY}`
          ); */

          // Find the root thread to recalculate the entire hierarchy
          const rootThread = findRootThread(currentTree, info.node.id);
          if (rootThread) {
            const updatedRootThread = recalculateThreadPositions(rootThread);

            // Replace the root thread in the tree
            const roleNode = currentTree.children.find((role) =>
              role.children.some(
                (thread) => thread.node.id === rootThread.node.id
              )
            );
            if (roleNode) {
              const threadIndex = roleNode.children.findIndex(
                (thread) => thread.node.id === rootThread.node.id
              );
              roleNode.children[threadIndex] = updatedRootThread;
            }
          }
        }
      }
    });
  });

  return currentTree;
};

const computeThreadPositions = ({ radTree, connections = [], thread }) => {
  let previousY = 0;

  const updatedChildren = thread.children
    .map((child, index) => {
      if (child.node.type === "void") {
        return {
          ...child,
          node: {
            ...child.node,
            position: { x: 0, y: 0 },
            offsetFromPrevious: 0,
          },
        };
      }

      if (child.node.class.includes("activity")) {
        // Calculate offset from previous activity (or start of thread for first activity)
        const offsetFromPrevious =
          index === 0 ? offsetActivityY : offsetActivityY;
        const currentY = previousY + offsetFromPrevious;

        const positionedActivity = {
          ...child,
          node: {
            ...child.node,
            position: { x: 0, y: currentY },
            offsetFromPrevious, // Store the incremental offset
          },
        };

        previousY = currentY + child.node.size.height;
        return positionedActivity;
      } else {
        // Refinement nodes
        const offsetFromPrevious =
          index === 0 ? offsetActivityY : offsetActivityY;
        const currentY = previousY + offsetFromPrevious;

        let currentX = offsetRefinementX;
        const updatedSubthreads = child.children.map((subthread) => {
          const positionedSubthread = {
            ...subthread,
            node: {
              ...subthread.node,
              position: { x: currentX, y: 0 },
            },
          };
          currentX += subthread.node.size.width + spacing;
          return positionedSubthread;
        });

        const positionedRefinement = {
          ...child,
          node: {
            ...child.node,
            position: { x: 0, y: currentY },
            offsetFromPrevious,
          },
          children: updatedSubthreads.map((subthread) =>
            computeThreadPositions({
              radTree,
              connections,
              thread: subthread,
            })
          ),
        };

        previousY = currentY + child.node.size.height;
        return positionedRefinement;
      }
    })
    .filter(Boolean);

  return { ...thread, children: updatedChildren };
};

const computeTreePositions = ({ tree }) => {
  const positionedTree = {
    ...tree,
    children: tree.children.map((role) => ({
      ...role,
      children: role.children.map((thread) =>
        computeThreadPositions({ tree, thread })
      ),
    })),
  };

  return positionedTree;
};

const computePositions = ({ tree, connections = [] }) => {
  // Pass 1: Calculate and apply initial positions
  const positionedTree = computeTreePositions({ tree });

  // Pass 2: Adjust for connections between interactions
  //console.log("Positioned tree before connections:", positionedTree);
  const connectedTree = adjustAllConnectionsInSequence({
    tree: positionedTree,
    connections,
  });
  //console.log("Connected tree:", connectedTree);
  // Pass 3: Set absolute positions on final tree
  const finalTree = adjustAbsolutePositions({ tree: connectedTree });

  return finalTree;
};

const computeLayout = (radTree, connections) => {
  try {
    const sizedTree = computeSizes(radTree);
    // First pass to position activities and refinements
    const positionedTree = computePositions({
      tree: sizedTree,
      connections,
    });

    return computeContainerSizes(positionedTree);
  } catch (error) {
    console.error("Error during layout computation:", error);
  }
};

export { computeLayout };
